<template>
    <div>
        <ContractPreview
            v-if="showPreview"
            ref="pdfViewer"
            :url="pdfUrl"
            v-on:close="showPreview = false"
        />

        <div v-if="state === 'loading'">
            <Loader></Loader>
        </div>

        <div v-if="!showPreview && customContract.property && state !== 'loading'" class="status-content">
            <Header :title="addressLine1"
                    :subtitle="customContract.property.zip_code + ' ' + customContract.property.city"
                    link="/contracts#custom-contracts">
                <div slot="actions" class="actions">
                    <!-- Revoke -->
                    <button v-if="canRevoke" v-on:click="revoke()" class="transparent" :disabled="state === 'saving'">
                        {{ $t('button.contract.recall') }}
                    </button>
                    <!-- Preview -->
                    <button v-on:click="preview()" class="transparent" :disabled="state === 'saving'">
                        {{ $t('button.contract.preview') }}
                    </button>
                    <a class="circle-icon">
                        <table-dropdown>
                            <li v-on:click="download()" :disabled="state === 'saving'">{{
                                    $t('button.download.label')
                                }}
                            </li>
                            <li v-on:click="destroy()" :disabled="state === 'saving'">{{
                                    $t('general.delete.button')
                                }}
                            </li>
                        </table-dropdown>
                    </a>
                </div>
            </Header>

            <div class="inner main-content">

                <Grid :fields="gridFields"/>

                <div class="status-row four-items" :class="{'three-items': customContract.requires_landlord_approval}">

                    <!-- Contract column -->
                    <StatusCol class="finished">
					<span slot="icon">
						<SvgIcon icon="contract-icon"/>
					</span>
                        <span slot="label">{{ $t('general.label.contract') }}</span>
                        <div class="status-circle" slot="status">
                            <div class="icon">
                                <SvgIcon icon="white-checkmark"/>
                            </div>
                        </div>
                    </StatusCol>

                    <!-- Approval column -->
                    <StatusCol v-if="customContract.requires_landlord_approval"
                               :class="{ progress: !customContract.is_approved_by_landlord, finished: customContract.is_approved_by_landlord }"
                    >
                    <span slot="icon">
						<SvgIcon icon="watch-icon"/>
					</span>
                        <span slot="label">{{ $t('digital.tabs.review.approval-status') }}</span>
                        <div class="status-circle" slot="status">
                            <div class="icon" v-if="customContract.is_approved_by_landlord">
                                <SvgIcon icon="white-checkmark"/>
                            </div>
                            <div class="icon" v-else>
                                <SvgIcon icon="loading"/>
                            </div>
                        </div>
                        <div class="list" slot=list>
                            <ContractStatusItemList :list="approvalStatusList"/>
                            <button
                                class="purple"
                                v-if="customContract.requires_landlord_approval && customContract.is_approved_by_landlord && !isSentToTenants"
                                v-on:click="sendForSigning()"
                                :disabled="state === 'saving'"
                            >
                                {{ $t('digital.tabs.sendemail') }}
                            </button>
                        </div>
                    </StatusCol>

                    <!-- Signing column -->
                    <StatusCol
                        v-bind:class="{ progress: signingStatusPercent !== 100, finished: signingStatusPercent === 100 }"
                    >
                        <span slot="inner-status" v-bind:style="{ width: signingStatusPercent + '%' }"/>
                        <span slot="icon">
						    <SvgIcon icon="pen-icon"/>
					    </span>
                        <span slot="label">{{ $t('general.label.signing') }}</span>
                        <div class="status-circle" slot="status">
                            <div class="icon" v-if="signingStatusPercent !== 100 ">
                                <SvgIcon icon="loading"/>
                            </div>
                            <div class="icon" v-else>
                                <SvgIcon icon="white-checkmark"/>
                            </div>
                        </div>
                        <div class="list" slot=list>
                            <ContractStatusItemList :list="signingStatusList"/>
                        </div>
                    </StatusCol>

                    <!-- Finished column -->
                    <StatusCol v-bind:class="{ finished: customContract.is_completed }">
					<span slot="icon">
						<SvgIcon icon="check-icon"/>
					</span>
                        <span slot="label">{{ $t('general.label.finished') }}</span>
                        <div class="status-circle" slot="status">
                            <div class="icon" v-if="customContract.is_completed">
                                <SvgIcon icon="white-checkmark"/>
                            </div>
                            <div class="icon" v-else>
                                <SvgIcon icon="loading"/>
                            </div>
                        </div>
                    </StatusCol>

                </div>

            </div>
        </div>

        <download-contract-modal
            v-if="showDownloadModal"
            v-on:close="showDownloadModal = false"
            :url="pdfUrl"
        />
    </div>
</template>

<script>
import Loader from "../../components/Loader";
import Header from "../../components/Header";
import TableDropdown from '../../components/TableDropdown';
import Grid from '../../components/Grid.vue';
import StatusCol from '../../components/StatusCol';
import SvgIcon from '../../components/Svg';
import ContractStatusItemList from '../../components/ContractStatusItemList';
import ContractPreview from '../../components/ContractPreview.vue';
import customContractMixin, {SENT_TO_TENANT} from '../../mixins/custom-contract';
import DownloadContractModal from "../../components/DownloadContractModal";

import {mapGetters} from "vuex";

export default {
    name: "CustomContractStatusDashboard",
    mixins: [customContractMixin],
    components: {
        Header,
        TableDropdown,
        Grid,
        StatusCol,
        SvgIcon,
        ContractStatusItemList,
        ContractPreview,
        Loader,
        DownloadContractModal
    },
    props: {},
    filters: {
        tenantName: function (tenants) {
            let name = tenants[0].full_name || tenants[0].email;

            if (tenants.length > 1)
                name += ' + ' + (tenants.length - 1);

            return name;
        }
    },
    data: function () {
        return {
            customContract: {}, // This component could be used
            showPreview: false,
            state: 'loading',
            canRevoke: true,
            canDisapprove: true,
            showDownloadModal: false
        }
    },
    computed: {
        signingStatusPercent() {
            let checked = _(this.signingStatusList).filter((status) => status.checked === true).size();
            let total = _(this.signingStatusList).size();

            return checked * 100 / total;
        },
        addressLine1() {
            var address1 = this.customContract.property.address ? this.customContract.property.address.replace(this.customContract.property.zip_code, '').replace(this.customContract.property.city, '') : '';
            var length = address1.length - 3;
            var converted = address1.slice(0, length);
            if (address1) {
                return converted;
            }
            return '';
        },
        gridFields() {
            return [
                {
                    title: this.$t('contract-status.current-status.label'),
                    value: this.getContractStatusText(this.customContract)
                },
                {
                    title: this.$t('contract-status.tenant.label'),
                    value: this.$options.filters.tenantName(this.customContract.tenants)
                },
                {
                    title: this.$t('contract-status.landlord.label'),
                    value: this.customContract.landlord ? this.customContract.landlord.name : this.customContract.landlord_email
                }
            ];
        },
        approvalStatusList() {
            return [
                {
                    checked: this.customContract.is_landlord_approval_requested,
                    label: this.$t('contract-status.sent-to-landlord-for-approval.label'),
                    tooltips: [],
                },
                {
                    checked: this.customContract.is_landlord_approval_request_received || this.customContract.was_viewed_by_landlord,
                    label: this.$t('contract-status.landlord-email.label'),
                    tooltips: [],
                },
                {
                    checked: this.customContract.was_viewed_by_landlord,
                    label: this.$t('contract-status.landlord-seen-contract.label'),
                    tooltips: [],
                },
                {
                    checked: this.customContract.is_approved_by_landlord,
                    label: this.$t('contract-status.landlord-has-approved.label'),
                    tooltips: [],
                },
            ]
        },
        signingStatusList() {
            if (this.customContract.tenants.length > 1) {
                // Multitenant

                let totalTenants = this.customContract.tenants.length;
                let totalTenantsSent = _(this.customContract.tenants).filter((tenant) => tenant.is_sent).size();
                let totalTenantsOpenedEmail = _(this.customContract.tenants).filter((tenant) => tenant.is_received || tenant.was_viewed).size();
                let totalTenantsSeen = _(this.customContract.tenants).filter((tenant) => tenant.was_viewed).size();
                let totalTenantsSigned = _(this.customContract.tenants).filter((tenant) => tenant.is_signed).size();

                return [
                    {
                        inProgress: totalTenantsSent > 0,
                        checked: totalTenantsSent === totalTenants,
                        label: this.$t('contract-status.sent-to.label') + ' ' + totalTenantsSent + ' ' + this.$t('contract-status.tenants-for-signing.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.customContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.is_sent}
                                }
                            )
                        },
                    },
                    {
                        inProgress: totalTenantsOpenedEmail > 0,
                        checked: totalTenantsOpenedEmail === totalTenants,
                        label: totalTenantsOpenedEmail + ' / ' + totalTenants + ' ' + this.$t('contract-status.tenants-email.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.customContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.is_received || tenant.was_viewed}
                                }
                            )
                        },
                    },
                    {
                        inProgress: totalTenantsSeen > 0,
                        checked: totalTenantsSeen === totalTenants,
                        label: totalTenantsSeen + ' / ' + totalTenants + ' ' + this.$t('contract-status.tenants-seen-contract.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.customContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.was_viewed}
                                }
                            )
                        },
                    },
                    {
                        inProgress: totalTenantsSigned > 0,
                        checked: totalTenantsSigned === totalTenants,
                        label: totalTenantsSigned + ' / ' + totalTenants + ' ' + this.$t('contract-status.tenants-signed-contract.label'),
                        tooltips: {
                            multiple: true,
                            items: _(this.customContract.tenants).map(function (tenant) {
                                    return {text: tenant.email, checked: tenant.is_signed}
                                }
                            )
                        },
                    },
                    {
                        checked: this.customContract.is_completed,
                        label: this.$t('contract-status.landlord-signed-contract.label'),
                    },
                ]

            } else {
                // Single Tenant

                let is_signed = this.customContract.tenants[0].is_signed;

                let result = [
                    {
                        checked: this.customContract.tenants[0].is_sent,
                        label: this.$t('contract-status.sent-to-tenant-for-signing.label'),
                    },
                    {
                        checked: this.customContract.tenants[0].is_received || this.customContract.tenants[0].was_viewed,
                        label: this.$t('contract-status.tenant-email.label'),
                    },
                    {
                        checked: this.customContract.tenants[0].was_viewed,
                        label: this.$t('contract-status.tenant-seen-contract.label'),
                    },
                    {
                        checked: this.customContract.tenants[0].is_signed,
                        label: this.$t('contract-status.tenant-signed-contract.label'),
                    }];

                if (this.customContract.has_extra_tenant_signature) {
                    result.push({
                        checked: this.customContract.is_signed_by_extra_tenant,
                        label: this.$t('contract-status.extra-tenant-signed-contract.label'),
                    });

                }

                result.push({
                    checked: this.customContract.is_completed,
                    label: this.$t('contract-status.landlord-signed-contract.label'),
                });

                return result;
            }

        },
        pdfUrl: function () {
            let customContractId = _.has(this.customContract, 'id') ? this.customContract.id : this.$route.params.id;

            return window.config.apiUrl + `api/custom-contract/${customContractId}.pdf?token=` + this.getToken;
        },
        isSentToTenants() {
            this.getContractStatusSet(this.customContract).includes(SENT_TO_TENANT);
        },
        ...mapGetters(['getToken'])
    },
    methods: {
        preview() {
            this.showPreview = true;
        },
        download() {
            this.showDownloadModal = true;
        },
        sendForSigning() {
            this.state = 'saving';

            axios({
                method: 'POST',
                url: `${window.config.apiUrl}api/custom-contract/${this.customContract.id}/send`,
                cancelToken: window.AuthorizationCancelSource.token,
                data: {
                    for: 'signing',
                }
            }).then(response => {
                Object.assign(this.customContract, response.data.data);
            }).finally(() => {
                this.state = 'ready';
            });
        }
    },
    mounted() {
        axios({
            method: 'GET',
            url: window.config.apiUrl + `api/custom-contract/${this.$route.params.id}`
        }).then((response) => {
            this.customContract = response.data.data;
            this.state = 'ready';
        });
    }
}
</script>

<style scoped>

</style>
